import type { AssessmentResult, LocalProgress, Scenario } from './types';

const STORAGE_KEY = 'the-line:progress:v1';

export const EMPTY_PROGRESS: LocalProgress = {
  version: 1,
  attempts: 0,
  bestScore: 0,
  highestLevel: 1,
  completedScenarioIds: [],
  updatedAt: '',
};

export function loadProgress(): LocalProgress {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<LocalProgress>;
    if (parsed.version !== 1) return { ...EMPTY_PROGRESS };
    return {
      version: 1,
      attempts: Math.max(0, Number(parsed.attempts) || 0),
      bestScore: Math.max(0, Math.min(100, Number(parsed.bestScore) || 0)),
      highestLevel: Math.max(1, Math.min(5, Number(parsed.highestLevel) || 1)),
      completedScenarioIds: Array.isArray(parsed.completedScenarioIds)
        ? parsed.completedScenarioIds.filter((id): id is string => typeof id === 'string')
        : [],
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : '',
    };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

export function saveResult(
  current: LocalProgress,
  result: AssessmentResult,
  scenarios: Scenario[],
): LocalProgress {
  const next: LocalProgress = {
    version: 1,
    attempts: current.attempts + 1,
    bestScore: Math.max(current.bestScore, result.total),
    highestLevel: Math.max(current.highestLevel, result.levelNumber),
    completedScenarioIds: [
      ...new Set([...current.completedScenarioIds, ...scenarios.map((scenario) => scenario.id)]),
    ],
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // The assessment remains fully usable when storage is unavailable.
  }
  return next;
}

export function clearProgress(): LocalProgress {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // No-op: the caller still resets in-memory progress.
  }
  return { ...EMPTY_PROGRESS };
}
